import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import { X, Check, Settings, CalendarClock } from 'lucide-react';
import { categoriasCostoFijoAPI, costosFijosAPI, periodosServicioAPI } from '../services/api';
import CategoriasManagerModal from './CategoriasManagerModal';
import CostoFijoPeriodosModal from './CostoFijoPeriodosModal';
import { invalidateCostosFijos } from '../services/cachedCatalogs';
import { selectStyles } from '../utils/adminShared';
import { useConfirm, Tooltip } from './ui';

const selectStylesTall = {
    ...selectStyles,
    control: (base, state) => ({
        ...selectStyles.control(base, state),
        minHeight: '42px',
    }),
};

const CostosFijosPanel = () => {
    const confirm = useConfirm();
    const [loading, setLoading] = useState(true);
    const [categoriasCostoFijo, setCategoriasCostoFijo] = useState([]);
    const [periodos, setPeriodos] = useState([]);
    const [costosFijos, setCostosFijos] = useState([]);
    const [nuevoCostoFijo, setNuevoCostoFijo] = useState({ nombre: '', costo: 0, categoria_id: '' });
    const [showCategoriasCFModal, setShowCategoriasCFModal] = useState(false);
    const [periodosModalCF, setPeriodosModalCF] = useState(null);
    // Filtros y paginacion
    const [filtroCategoria, setFiltroCategoria] = useState(null);
    const [filtroPeriodo, setFiltroPeriodo] = useState(null);
    const [busqueda, setBusqueda] = useState('');
    const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
    const [pageSize, setPageSize] = useState(25);
    const [currentPage, setCurrentPage] = useState(1);

    // Cambios pendientes por costo fijo — requieren confirmacion explicita
    const [pendingCF, setPendingCF] = useState({});

    const getCFVal = (cf, field) => (
        pendingCF[cf.id]?.[field] !== undefined ? pendingCF[cf.id][field] : cf[field] ?? ''
    );
    const setCFVal = (id, field, value) => {
        setPendingCF(prev => ({ ...prev, [id]: { ...(prev[id] || {}), [field]: value } }));
    };
    const hasCFChanges = (id) => Boolean(pendingCF[id] && Object.keys(pendingCF[id]).length > 0);
    const confirmCFChanges = async (id) => {
        const changes = pendingCF[id];
        if (!changes) return;
        await actualizarCostoFijo(id, changes);
        setPendingCF(prev => { const n = { ...prev }; delete n[id]; return n; });
        toast.success('Costo fijo actualizado');
    };
    const discardCFChanges = (id) => {
        setPendingCF(prev => { const n = { ...prev }; delete n[id]; return n; });
    };

    useEffect(() => {
        cargarDatos();
    }, []);

    const cargarDatos = async () => {
        setLoading(true);
        try {
            const [categoriasCFRes, costosFijosRes, periodosRes] = await Promise.all([
                categoriasCostoFijoAPI.getAll(true),
                costosFijosAPI.getAll(true),
                periodosServicioAPI.getAll(true).catch(() => ({ data: [] }))
            ]);
            setCategoriasCostoFijo(categoriasCFRes.data || []);
            setCostosFijos(costosFijosRes.data || []);
            setPeriodos(periodosRes.data || []);
        } catch (err) {
            console.error('Error al cargar datos:', err);
        } finally {
            setLoading(false);
        }
    };

    // === COSTOS FIJOS ===
    const agregarCostoFijo = async () => {
        if (!nuevoCostoFijo.nombre.trim()) {
            toast.error('El nombre es obligatorio');
            return;
        }
        if (!nuevoCostoFijo.categoria_id) {
            toast.error('La categoría es obligatoria');
            return;
        }
        try {
            await costosFijosAPI.create(nuevoCostoFijo);
            invalidateCostosFijos();
            setNuevoCostoFijo({ nombre: '', costo: 0, categoria_id: '' });
            cargarDatos();
            toast.success('Costo fijo creado');
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    const actualizarCostoFijo = async (id, data) => {
        try {
            await costosFijosAPI.update(id, data);
            invalidateCostosFijos();
            setCostosFijos(prev => prev.map(cf => cf.id === id ? { ...cf, ...data } : cf));
        } catch (err) {
            toast.error('Error: ' + err.message);
            cargarDatos();
        }
    };

    const eliminarCostoFijo = async (id, nombre) => {
        const ok = await confirm({
            title: '¿Eliminar costo fijo?',
            message: <p>Vas a eliminar el costo fijo <strong>{nombre}</strong>. Esta acción no se puede deshacer.</p>,
            confirmText: 'Eliminar',
            danger: true,
        });
        if (!ok) return;
        try {
            await costosFijosAPI.delete(id);
            invalidateCostosFijos();
            toast.success('Costo fijo eliminado');
            cargarDatos();
        } catch (err) {
            toast.error('Error: ' + err.message);
        }
    };

    // Periodos con precio activo. Sin ninguno, el costo fijo usa su precio unico
    const preciosActivos = (cf) => (cf.precios_periodo || []).filter(p => p.activo);

    // Filtros + paginacion client-side
    const busquedaNorm = busqueda.trim().toLowerCase();
    const costosFiltrados = costosFijos.filter(cf => {
        if (filtroCategoria && cf.categoria_id !== filtroCategoria) return false;
        if (filtroPeriodo && !preciosActivos(cf).some(p => p.periodo_id === filtroPeriodo)) return false;
        if (busquedaNorm && !(String(cf.nombre || '').toLowerCase().includes(busquedaNorm))) return false;
        return true;
    });
    const totalPaginas = Math.max(1, Math.ceil(costosFiltrados.length / pageSize));
    const paginaActual = Math.min(currentPage, totalPaginas);
    const costosPagina = costosFiltrados.slice((paginaActual - 1) * pageSize, paginaActual * pageSize);
    useEffect(() => { setCurrentPage(1); }, [filtroCategoria, filtroPeriodo, pageSize, busqueda]);

    if (loading) {
        return <div className="loading">Cargando costos fijos...</div>;
    }

    return (
    <>
        <div className="card">
            <div className="card-header">
                <h2>Costos Fijos</h2>
            </div>
            <div className="admin-content">
                <div className="admin-section-header">
                    <h3 className="h3">Agregar Costo Fijo</h3>
                    <button
                        className="btn small btn-outline-primary"
                        type="button"
                        onClick={() => setShowCategoriasCFModal(true)}
                    >
                        <Settings size={14} />
                        Gestionar Categorías
                    </button>
                </div>
                <div className="servicios-form-grid cols-4 inline-submit">
                    <label className="field">
                        <span className="label">Nombre del servicio *</span>
                        <input
                            className="input"
                            type="text"
                            value={nuevoCostoFijo.nombre}
                            onChange={(e) => setNuevoCostoFijo(n => ({ ...n, nombre: e.target.value }))}
                        />
                    </label>
                    <label className="field">
                        <span className="label">Costo</span>
                        <input
                            className="input"
                            type="number"
                            min="0"
                            step="0.01"
                            value={nuevoCostoFijo.costo}
                            onChange={(e) => setNuevoCostoFijo(n => ({ ...n, costo: parseFloat(e.target.value) || 0 }))}
                        />
                    </label>
                    <div className="field">
                        <span className="label">Categoría *</span>
                        <Select
                            styles={selectStylesTall}
                            options={categoriasCostoFijo.filter(c => c.activo).map(cat => ({
                                value: cat.id,
                                label: cat.nombre
                            }))}
                            value={
                                nuevoCostoFijo.categoria_id
                                    ? { value: nuevoCostoFijo.categoria_id, label: categoriasCostoFijo.find(c => c.id === parseInt(nuevoCostoFijo.categoria_id))?.nombre || '' }
                                    : null
                            }
                            onChange={(selected) => setNuevoCostoFijo(n => ({ ...n, categoria_id: selected?.value || '' }))}
                            placeholder="Seleccionar categoría..."
                            isSearchable
                            noOptionsMessage={() => 'Sin opciones'}
                        />
                    </div>
                    <button className="btn neon servicios-form-submit" type="button" onClick={agregarCostoFijo}>
                        + Agregar
                    </button>
                </div>

                <div className="divider" />

                <div className="admin-section-header">
                    <h3 className="h3">Costos Fijos Existentes ({costosFiltrados.length})</h3>
                    <div className="admin-filtros-inline">
                        <input
                            className="input admin-buscador-inline"
                            type="search"
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            placeholder="Buscar por nombre..."
                            aria-label="Buscar costo fijo"
                        />
                        <Select
                            styles={selectStyles}
                            options={[
                                { value: null, label: 'Todas las categorías' },
                                ...categoriasCostoFijo.filter(c => c.activo).map(cat => ({ value: cat.id, label: cat.nombre }))
                            ]}
                            value={
                                filtroCategoria
                                    ? { value: filtroCategoria, label: categoriasCostoFijo.find(c => c.id === filtroCategoria)?.nombre || '' }
                                    : { value: null, label: 'Todas las categorías' }
                            }
                            onChange={(sel) => setFiltroCategoria(sel?.value || null)}
                            placeholder="Categoría..."
                            isSearchable={false}
                        />
                        <Select
                            styles={selectStyles}
                            options={[
                                { value: null, label: 'Todos los períodos' },
                                ...periodos.map(p => ({ value: p.id, label: p.nombre }))
                            ]}
                            value={
                                filtroPeriodo
                                    ? { value: filtroPeriodo, label: periodos.find(p => p.id === filtroPeriodo)?.nombre || '' }
                                    : { value: null, label: 'Todos los períodos' }
                            }
                            onChange={(sel) => setFiltroPeriodo(sel?.value || null)}
                            placeholder="Período..."
                            isSearchable={false}
                        />
                    </div>
                </div>
                <div className="admin-list">
                    {costosPagina.map(cf => {
                        const dirty = hasCFChanges(cf.id);
                        const catId = getCFVal(cf, 'categoria_id');
                        return (
                        <div key={cf.id} className={`admin-item ${!cf.activo ? 'inactive' : ''} ${dirty ? 'has-pending-changes' : ''}`}>
                            <div className="admin-item-content">
                                <input
                                    className="input"
                                    type="text"
                                    value={getCFVal(cf, 'nombre')}
                                    onChange={(e) => setCFVal(cf.id, 'nombre', e.target.value)}
                                />
                                <div className="field-inline">
                                    <span className="label">Costo:</span>
                                    <input
                                        className="input input-sm"
                                        type="number"
                                        min="0"
                                        step="0.01"
                                        value={getCFVal(cf, 'costo')}
                                        onChange={(e) => setCFVal(cf.id, 'costo', parseFloat(e.target.value) || 0)}
                                    />
                                </div>
                                <div className="admin-item-select">
                                    <Select
                                        styles={selectStylesTall}
                                        options={[
                                            { value: '', label: 'Sin categoría' },
                                            ...categoriasCostoFijo.filter(c => c.activo).map(cat => ({
                                                value: cat.id,
                                                label: cat.nombre
                                            }))
                                        ]}
                                        value={
                                            catId
                                                ? { value: catId, label: categoriasCostoFijo.find(c => c.id === catId)?.nombre || '' }
                                                : { value: '', label: 'Sin categoría' }
                                        }
                                        onChange={(selected) => setCFVal(cf.id, 'categoria_id', selected?.value || null)}
                                        placeholder="Categoría..."
                                        isSearchable
                                        noOptionsMessage={() => 'Sin opciones'}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                </div>
                                <div className="field-inline admin-item-periodos">
                                    <span className="label">Períodos:</span>
                                    {(() => {
                                        const activos = preciosActivos(cf).length;
                                        return (
                                            <button
                                                className="btn ghost small"
                                                type="button"
                                                onClick={() => setPeriodosModalCF(cf)}
                                            >
                                                <CalendarClock size={14} />
                                                {activos === 0
                                                    ? 'Precio único'
                                                    : `${activos} ${activos === 1 ? 'activo' : 'activos'}`}
                                            </button>
                                        );
                                    })()}
                                </div>
                            </div>
                            <div className="admin-item-actions">
                                {dirty && (
                                    <>
                                        <button
                                            className="btn success small"
                                            onClick={() => confirmCFChanges(cf.id)}
                                        >
                                            <Check size={14} />
                                            Actualizar
                                        </button>
                                        <Tooltip content="Descartar cambios">
                                            <button
                                                className="btn ghost small"
                                                onClick={() => discardCFChanges(cf.id)}
                                                aria-label="Descartar cambios"
                                            >
                                                <X size={14} />
                                            </button>
                                        </Tooltip>
                                    </>
                                )}
                                <button
                                    className={`btn ghost small ${cf.activo ? '' : 'success'}`}
                                    onClick={() => actualizarCostoFijo(cf.id, { activo: !cf.activo })}
                                >
                                    {cf.activo ? 'Desactivar' : 'Activar'}
                                </button>
                                <button
                                    className="btn ghost small danger"
                                    onClick={() => eliminarCostoFijo(cf.id, cf.nombre)}
                                >
                                    Eliminar
                                </button>
                            </div>
                        </div>
                        );
                    })}
                    {costosFiltrados.length === 0 && (
                        <p className="empty-message">
                            No hay costos fijos{(filtroCategoria || filtroPeriodo) ? ' con esos filtros' : ' creados'}
                        </p>
                    )}
                </div>

                {costosFiltrados.length > 0 && (
                    <div className="admin-pagination">
                        <div className="admin-pagination-info">
                            Mostrando <strong>{(paginaActual - 1) * pageSize + 1}</strong>
                            {' – '}
                            <strong>{Math.min(paginaActual * pageSize, costosFiltrados.length)}</strong>
                            {' de '}
                            <strong>{costosFiltrados.length}</strong>
                        </div>
                        <div className="admin-pagination-controls">
                            <label className="admin-pagination-size">
                                <span className="text-xs text-muted">Por página:</span>
                                <select
                                    className="input"
                                    value={pageSize}
                                    onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                                >
                                    {PAGE_SIZE_OPTIONS.map(n => <option key={n} value={n}>{n}</option>)}
                                </select>
                            </label>
                            <div className="admin-pagination-buttons">
                                <button type="button" className="btn ghost sm" disabled={paginaActual === 1} onClick={() => setCurrentPage(1)}>« Inicio</button>
                                <button type="button" className="btn ghost sm" disabled={paginaActual === 1} onClick={() => setCurrentPage(p => Math.max(1, p - 1))}>‹</button>
                                <span className="admin-pagination-current">
                                    Página <strong>{paginaActual}</strong> de <strong>{totalPaginas}</strong>
                                </span>
                                <button type="button" className="btn ghost sm" disabled={paginaActual === totalPaginas} onClick={() => setCurrentPage(p => Math.min(totalPaginas, p + 1))}>›</button>
                                <button type="button" className="btn ghost sm" disabled={paginaActual === totalPaginas} onClick={() => setCurrentPage(totalPaginas)}>Fin »</button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>

        {/* Modal de Categorías de Costo Fijo — patrón unificado */}
        <CategoriasManagerModal
            open={showCategoriasCFModal}
            onClose={() => setShowCategoriasCFModal(false)}
            onChanged={cargarDatos}
            title="Gestionar categorías de costo fijo"
            placeholder="Ej: Licencias de software"
            countField="costos_count"
            countLabel="costo fijo"
            api={{
                list: () => categoriasCostoFijoAPI.getAll(true),
                create: (data) => categoriasCostoFijoAPI.create(data),
                update: (id, data) => categoriasCostoFijoAPI.update(id, data),
                delete: (id) => categoriasCostoFijoAPI.delete(id)
            }}
        />

        {/* Precios por período — reemplaza duplicar el costo fijo por cada recurrencia */}
        <CostoFijoPeriodosModal
            open={!!periodosModalCF}
            onClose={() => setPeriodosModalCF(null)}
            onSaved={() => { invalidateCostosFijos(); cargarDatos(); }}
            costoFijo={periodosModalCF}
            periodos={periodos}
        />
    </>
    );
};

export default CostosFijosPanel;
